import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { getTrendsByCategory } from './trendsRegistry'

// Main Education Trends Container
function EducationTrends({ trendsData }) {
  // State for trend type filter
  const [selectedTrendType, setSelectedTrendType] = useState('all')

  // Get all available education trends from registry
  const availableTrends = useMemo(() => {
    return getTrendsByCategory('education')
  }, [])

  // Filter trends based on selected type
  const filteredTrends = useMemo(() => {
    if (selectedTrendType === 'all') {
      return availableTrends
    }

    return availableTrends.filter(trend => {
      if (selectedTrendType === 'secondary') {
        return trend.id.includes('secondary')
      } else if (selectedTrendType === 'primary') {
        return trend.id.includes('primary')
      } else if (selectedTrendType === 'ple') {
        return trend.id.includes('ple')
      } else if (selectedTrendType === 'institution') {
        return trend.id.includes('institution')
      }
      return true
    })
  }, [availableTrends, selectedTrendType])

  // Get trend type options for react-select
  const trendTypeOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Trends' }]

    const hasSecondary = availableTrends.some(trend =>
      trend.id.includes('secondary')
    )
    const hasPrimary = availableTrends.some(trend =>
      trend.id.includes('primary')
    )
    const hasPLE = availableTrends.some(trend => trend.id.includes('ple'))
    const hasInstitution = availableTrends.some(trend =>
      trend.id.includes('institution')
    )

    if (hasSecondary) {
      options.push({ value: 'secondary', label: 'Secondary School' })
    }
    if (hasPrimary) {
      options.push({ value: 'primary', label: 'Primary School' })
    }
    if (hasPLE) {
      options.push({ value: 'ple', label: 'PLE Analysis' })
    }
    if (hasInstitution) {
      options.push({ value: 'institution', label: 'Institution Count' })
    }

    return options
  }, [availableTrends])

  // Get selected option for react-select
  const selectedOption = trendTypeOptions.find(
    option => option.value === selectedTrendType
  )

  if (!availableTrends || availableTrends.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Education Trends
        </h2>
        <p className="text-gray-600">
          No education trend visualizations available
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Education Trends
            </h2>
            <p className="text-gray-600">
              Comprehensive analysis of educational trends across different
              metrics and time periods
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-600">
              {filteredTrends.length}
            </div>
            <div className="text-sm text-gray-600">
              {selectedTrendType === 'all'
                ? 'Trend Analysis'
                : `${trendTypeOptions.find(opt => opt.value === selectedTrendType)?.label || 'Trends'}`}
            </div>
          </div>
        </div>

        {/* Trend Type Filter */}
        {trendTypeOptions.length > 1 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <div className="w-48">
                <Select
                  value={selectedOption}
                  onChange={option => setSelectedTrendType(option.value)}
                  options={trendTypeOptions}
                  className="text-sm"
                  classNamePrefix="select"
                  isSearchable={false}
                  placeholder="Select trend type..."
                />
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {filteredTrends.length} of {availableTrends.length} trends shown
            </div>
          </div>
        )}
      </div>

      {/* Render each trend component */}
      {filteredTrends.map(trend => {
        const TrendComponent = trend.component
        const trendData = trendsData?.[trend.id]

        return (
          <div key={trend.id} className="space-y-4">
            <TrendComponent data={trendData} />
          </div>
        )
      })}

      {/* No Filtered Trends State */}
      {filteredTrends.length === 0 && availableTrends.length > 0 && (
        <div className="text-center py-12">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-blue-800 font-medium mb-2">No Trends Found</h3>
            <p className="text-blue-600 text-sm">
              No trends match the selected filter. Try selecting &quot;All
              Trends&quot; to see available options.
            </p>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!trendsData && (
        <div className="text-center py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-yellow-800 font-medium mb-2">No Data Loaded</h3>
            <p className="text-yellow-600 text-sm">
              Education trends data is loading or not available for the selected
              location.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

EducationTrends.propTypes = {
  trendsData: PropTypes.object,
}

export default EducationTrends
